import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import studentProfile from '../../assets/dashboard/studentProfile/image.png';

export const Topbar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser, logout } = useAuth();

  // State for dropdown visibility
  const [showDropdown, setShowDropdown] = useState(false);

  // State for search functionality
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  // State for notifications
  const [notifications, setNotifications] = useState([]);
  const [showNotifications, setShowNotifications] = useState(false);

  // Refs for handling clicks outside dropdown and notifications
  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const mobileSearchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Sample notifications for development
  const SAMPLE_NOTIFICATIONS = [
    // { id: 1, title: "New assignment", message: "Math homework due tomorrow", read: false, timestamp: new Date(Date.now() - 1000 * 60 * 30).toISOString() },
    // { id: 2, title: "Grade posted", message: "Your Science quiz has been graded", read: true, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2).toISOString() },
    // { id: 3, title: "Course announcement", message: "History class canceled on Friday", read: false, timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString() }
  ];

  // Load notifications on component mount
  useEffect(() => {
    setNotifications(SAMPLE_NOTIFICATIONS);
  }, []);

  // Focus search input when mobile search is opened
  useEffect(() => {
    if (showMobileSearch && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current.focus();
      }, 100);
    }
  }, [showMobileSearch]);

  // Handle clicks outside of dropdowns to close them
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }

      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setShowNotifications(false);
      }

      if (mobileSearchRef.current && !mobileSearchRef.current.contains(event.target)) {
        setShowMobileSearch(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Handle search input change
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  // Handle search submission
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    // navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    setShowMobileSearch(false);
    setSearchQuery(""); // Clear search after submission
  };

  // Handle escape key to close mobile search
  const handleSearchKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowMobileSearch(false);
    }
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Handle profile navigation
  const handleProfileClick = () => {
    navigate('/profile');
    setShowDropdown(false);
  };

  const handleCertificateClick = () => {
    navigate('/profile/certificate');
    setShowDropdown(false);
  };

  const handleChangePasswordClick = () => {
    navigate('/profile/change-password');
    setShowDropdown(false);
  };

  const handleFeesClick = () => {
    navigate('/profile/fees');
    setShowDropdown(false);
  };

  // Toggle user dropdown
  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
    if (showNotifications) setShowNotifications(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  // Toggle notifications panel
  const toggleNotifications = () => {
    setShowNotifications(!showNotifications);
    if (showDropdown) setShowDropdown(false);
    if (showMobileSearch) setShowMobileSearch(false);
  };

  // Toggle mobile search
  const toggleMobileSearch = () => {
    setShowMobileSearch(!showMobileSearch);
    if (showDropdown) setShowDropdown(false);
    if (showNotifications) setShowNotifications(false);
  };

  // Mark notification as read
  const markNotificationAsRead = (id) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  // Format timestamp for notifications
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();

    const diffMinutes = Math.floor((now - date) / (1000 * 60));
    const diffHours = Math.floor(diffMinutes / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMinutes < 60) {
      return `${diffMinutes} min ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hr ago`;
    } else {
      return `${diffDays} day ago`;
    }
  };

  // Get unread notifications count
  const unreadCount = notifications.filter(n => !n.read).length;

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!currentUser) return "SN";

    const firstName = currentUser.firstName || "";
    const lastName = currentUser.lastName || "";

    if (firstName && lastName) {
      return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
    } else if (firstName) {
      return firstName.substring(0, 2).toUpperCase();
    } else if (currentUser.email) {
      return currentUser.email.substring(0, 2).toUpperCase();
    }

    return "SN";
  };

  // Get display name
  const getDisplayName = () => {
    if (!currentUser) return "Student Name";

    if (currentUser.firstName || currentUser.lastName) {
      return `${currentUser.firstName || ""} ${currentUser.lastName || ""}`.trim();
    }

    if (currentUser.email) {
      return currentUser.email.split("@")[0];
    }

    return "Student";
  };

  return (
    <div className="flex flex-col items-start gap-2.5 px-2 sm:px-4 md:px-6 py-3 relative bg-white border-b [border-bottom-style:solid] border-neutralneutral-medium top-0 sticky z-50 h-[62px]">
      {/* Mobile search overlay */}
      {showMobileSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-5 z-40 md:hidden flex flex-col">
          <div
            className="bg-white w-full px-4 py-3 flex items-center gap-3 shadow-md"
            ref={mobileSearchRef}
          >
            <button
              onClick={toggleMobileSearch}
              className="p-2 rounded-full hover:bg-gray-100"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5"></path>
                <path d="M12 19l-7-7 7-7"></path>
              </svg>
            </button>

            <form
              onSubmit={handleSearchSubmit}
              className="flex-1 flex items-center gap-2"
            >
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                placeholder="Search..."
                className="w-full bg-gray-100 border-none rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300"
              />
            </form>

            <button
              onClick={handleSearchSubmit}
              disabled={!searchQuery.trim()}
              className={`p-2 rounded-full ${!searchQuery.trim() ? 'text-gray-400' : 'text-blue-500 hover:bg-blue-50'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"></circle>
                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
              </svg>
            </button>
          </div>
        </div>
      )}

      <div className="flex w-full justify-between flex-[0_0_auto] items-center relative">
        {/* Logo section */}
        <div className="inline-flex gap-2 md:gap-[68px] flex-[0_0_auto] items-center relative">
          <div
            className="relative w-[100px] sm:w-[130px] h-8 rounded cursor-pointer"
            onClick={() => navigate('/dashboard')}
          >
            {/* Logo placeholder */}
            <div className="w-full h-full bg-blue-200 rounded flex items-center justify-center">
              <span className="text-blue-600 font-bold text-xs sm:text-sm">Springlearns</span>
            </div>
          </div>
        </div>

        {/* Search bar - hide on mobile, show on larger screens */}
        <form
          className="hidden md:flex w-full max-w-[385px] justify-around gap-1 px-4 py-2 self-stretch bg-white rounded-[100px] border border-solid opacity-60 cursor-not-allowed"
          style={{ borderColor: '#d6d6d6' }}
          onSubmit={(e) => e.preventDefault()}
        >
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {}} // No-op function
            placeholder="Search functionality coming soon..."
            className="relative flex-1 font-body-2 text-gray-500 text-sm bg-white outline-none cursor-not-allowed"
            disabled
          />
          <div className="w-5 h-5 flex items-center justify-center text-gray-400">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
        </form>

        {/* Mobile search icon - disabled */}
        <div className="md:hidden">
          <div
            className="w-8 h-8 bg-gray-100 rounded flex items-center justify-center opacity-60 cursor-not-allowed"
            aria-label="Search disabled"
            title="Search coming soon"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
          </div>
        </div>

        {/* User section */}
        <div className="inline-flex gap-2 sm:gap-6 flex-[0_0_auto] items-center relative">
          {/* Notification bell */}
          <div
            className="relative w-8 h-8 bg-blue-100 rounded flex items-center justify-center cursor-pointer hover:bg-blue-200 transition-colors"
            onClick={toggleNotifications}
            ref={notificationsRef}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {unreadCount > 0 && (
              <div className="absolute w-4 h-4 top-0 right-0 bg-red-600 rounded-full flex items-center justify-center text-white text-[10px] font-bold">
                {unreadCount}
              </div>
            )}

            {/* Notifications dropdown */}
            {showNotifications && (
              <div className="absolute right-0 top-10 w-72 sm:w-80 bg-white rounded-md shadow-lg z-50 py-2 border border-gray-200 max-h-96 overflow-y-auto">
                <div className="px-4 py-2 border-b border-gray-100 flex justify-between items-center">
                  <h3 className="font-semibold text-sm">Notifications</h3>
                  {notifications.length > 0 && (
                    <button
                      className="text-xs text-blue-500 hover:underline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
                      }}
                    >
                      Mark all as read
                    </button>
                  )}
                </div>

                {notifications.length === 0 ? (
                  <div className="px-4 py-6 text-center text-gray-500 text-sm">
                    No notifications
                  </div>
                ) : (
                  notifications.map(notification => (
                    <div
                      key={notification.id}
                      className={`px-4 py-3 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 cursor-pointer ${!notification.read ? 'bg-blue-50' : ''}`}
                      onClick={() => markNotificationAsRead(notification.id)}
                    >
                      <div className="flex justify-between">
                        <h4 className="font-medium text-sm">{notification.title}</h4>
                        <span className="text-xs text-gray-500">{formatTimestamp(notification.timestamp)}</span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">{notification.message}</p>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* User profile with dropdown */}
          <div className="flex gap-2 items-center relative" ref={dropdownRef}>
            {/* Avatar - Show profile image if available, otherwise show initials */}
            <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center">
              {currentUser?.profileImage ? (
                <img
                  // src={currentUser.profileImage} 
                  src={studentProfile}
                  alt={getDisplayName()}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.parentNode.classList.add('bg-gradient-to-r', 'from-purple-500', 'to-blue-500');
                    e.target.parentNode.innerHTML = `<span class="text-white text-xs">${getUserInitials()}</span>`;
                  }}
                />
              ) : (
                <div className="w-full h-full rounded-full bg-gradient-to-r from-purple-500 to-blue-500 flex items-center justify-center text-white text-xs">
                  {/* {getUserInitials()} */}
                  <img
                    // src={currentUser.profileImage} 
                    src={studentProfile}
                    alt={getDisplayName()}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentNode.classList.add('bg-gradient-to-r', 'from-purple-500', 'to-blue-500');
                      e.target.parentNode.innerHTML = `<span class="text-white text-xs">${getUserInitials()}</span>`;
                    }} />
                </div>
              )}
            </div>

            {/* Username and dropdown - Hide username on small screens */}
            <div className="relative">
              <div
                className="inline-flex gap-1 items-center cursor-pointer"
                onClick={toggleDropdown}
              >
                <div className="hidden sm:block font-medium text-sm text-gray-800">
                  {getDisplayName()}
                </div>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className={`transition-transform duration-200 ${showDropdown ? 'rotate-180' : ''}`}
                >
                  <path d="M6 9l6 6 6-6"></path>
                </svg>
              </div>

              {/* Dropdown menu */}
              {showDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-50 py-1 border border-gray-200">
                  <button
                    onClick={handleProfileClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    View Profile
                  </button>
                  <button
                    // onClick={() => {console.log("Change Password");}}
                    onClick={handleChangePasswordClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Change Password
                  </button>
                  <button
                    // Removed onClick handler to make non-clickable
                    className="block w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                  >
                    Fees Payment
                  </button>
                  <button
                    // onClick={()=>{console.log("Certificates");}}
                    onClick={handleCertificateClick}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Certificates
                  </button>
                  <button
                    // Removed onClick handler to make non-clickable
                    className="block w-full text-left px-4 py-2 text-sm text-gray-400 cursor-not-allowed"
                  >
                    Referrals
                  </button>
                  {/*                   
                  <button
                    onClick={() => {
                      navigate('/settings');
                      setShowDropdown(false);
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </button> */}
                  <button
                    onClick={handleLogout}
                    className="block w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};